import { EVENTS } from "@archetype-themes/utils/events";

class ToggleCart extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    this.cartOpen = false;
    this.cartTrigger = this.querySelector(".js-cart-trigger");

    if (!this.cartTrigger) return;

    // Initialize ARIA attributes
    this.initAria();

    // Event listeners
    this.cartTrigger.addEventListener("click", this.handleClick.bind(this), {
      signal: this.abortController.signal,
    });

    document.addEventListener(
      EVENTS.ajaxProductAdded,
      (evt) => {
        this.updateCartCount(evt.detail.product);
        this.announceCartUpdate(evt.detail.product);
      },
      { signal: this.abortController.signal }
    );

    document.addEventListener(
      EVENTS.cartUpdated,
      (evt) => {
        this.updateCartCount(evt.detail.cart);
      },
      { signal: this.abortController.signal }
    );

    document.addEventListener(
      EVENTS.headerDrawerClosed,
      this.handleDrawerClosed.bind(this),
      {
        signal: this.abortController.signal,
      }
    );
  }

  initAria() {
    // Set initial ARIA attributes
    if (!this.cartTrigger.hasAttribute("aria-expanded")) {
      this.cartTrigger.setAttribute("aria-expanded", "false");
    }
    this.cartTrigger.setAttribute("role", "button");

    // Ensure controlled element exists
    const cartDrawer = document.getElementById(
      this.cartTrigger.getAttribute("aria-controls")
    );
    if (cartDrawer) {
      cartDrawer.setAttribute("aria-hidden", "true");
      cartDrawer.setAttribute("aria-modal", "true");
      cartDrawer.setAttribute("role", "dialog");
    }
  }

  handleClick(evt) {
    evt.preventDefault();
    this.cartOpen = !this.cartOpen;
    this.updateAria(this.cartOpen);

    const eventName = this.cartOpen ? EVENTS.cartOpen : EVENTS.cartClose;
    this.cartTrigger.dispatchEvent(
      new CustomEvent(eventName, { bubbles: true })
    );

    // Focus management
    if (this.cartOpen) {
      const cartDrawer = document.getElementById(
        this.cartTrigger.getAttribute("aria-controls")
      );
      const firstFocusable = cartDrawer.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      setTimeout(() => firstFocusable?.focus(), 100);
    }
  }

  updateAria(isOpen) {
    this.cartTrigger.setAttribute("aria-expanded", String(isOpen));

    const cartDrawer = document.getElementById(
      this.cartTrigger.getAttribute("aria-controls")
    );
    if (cartDrawer) {
      cartDrawer.setAttribute("aria-hidden", String(!isOpen));
      cartDrawer.inert = !isOpen;
    }
  }

  handleDrawerClosed() {
    this.cartOpen = false;
    this.updateAria(false);
  }

  updateCartCount(state) {
    const html = new DOMParser().parseFromString(
      state.sections["cart-ajax"],
      "text/html"
    );
    const newCartCount = html.querySelector(".cart-link__bubble");
    const cartCount = this.querySelector(".cart-link__bubble");
    if (!newCartCount || !cartCount) return;
    cartCount.outerHTML = newCartCount.outerHTML;
  }

  announceCartUpdate(product) {
    const announcement = document.createElement("div");
    announcement.setAttribute("aria-live", "polite");
    announcement.setAttribute("role", "status");
    announcement.className = "visually-hidden";
    announcement.textContent = `${product.title} ${
      "general.cart.added_to_cart" | t
    }`;
    document.body.appendChild(announcement);
    setTimeout(() => announcement.remove(), 1000);
  }

  disconnectedCallback() {
    this.abortController.abort();
  }
}

customElements.define("toggle-cart", ToggleCart);
